import { ChatApi } from "@/src/model/ChatApi";
import { CHAT_API_TYPE } from "@/src/model/apiTypes";

const chatApi = new ChatApi();
export default {
    namespaced: true,
    state: {
        enabled: false,
        conversations: [],
        conversationId: -1,
        emptyConversation: true,
        waitingForResponse: false,
        broken: false,
        messages: [],
        feedback: {
            sentiment: "neutral",
            text: "",
        },
        settings: {
            showHiddenMessages: false,
            sendWithEnter: false,
        },
        healthSummary: [],
        contextCache: [],
    },
    getters: {
        enabled: (state) => state.enabled,
        conversationId: (state) => state.conversationId,
        emptyConversation: (state) => state.emptyConversation,
        conversations: (state) => state.conversations,
        cleanMessages: (state) =>
            state.messages.filter((m) => (m.thread.agent == "root" || m.thread.id == -1) && !m.tool),
        messages: (state) => state.messages,
        open: (state) => !state.broken,
        feedback: (state) => state.feedback,
        settings: (state) => state.settings,
        healthSummary: (state) => state.healthSummary,
        contextCache: (state) => state.contextCache,
    },
    mutations: {
        setEnabled: (state, enabled) => {
            state.enabled = enabled;
        },
        waiting: (state) => {
            state.waitingForResponse = true;
        },
        gotResponse: (state) => {
            state.waitingForResponse = false;
        },
        setEmptyConversation: (state) => {
            state.emptyConversation = true;
        },
        updateSettings: (state, payload) => {
            for (let key in payload) {
                state.settings[key] = payload[key];
            }
        },
        updateConversations: (state, { conversations }) => {
            // create the conversation or update the conversations title
            for (let conversation of conversations) {
                let savedConversation = state.conversations.find((c) => c.id == conversation.id);
                if (savedConversation) {
                    savedConversation.alive = true;
                    savedConversation.title = conversation.title;
                } else {
                    conversation.alive = true;
                    state.conversations.push(conversation);
                }
            }
            // filter out the conversations that are not alive anymore
            state.conversations = state.conversations
                .filter((c) => c.alive || c.id == state.conversationId)
                .map((c) => {
                    return { id: c.id, created: c.created, title: c.title };
                });
        },
        setAdminConversations: (state, conversations) => {
            state.conversations = conversations;
        },
        clearCurrentConversation: (state) => {
            state.conversationId = -1;
            state.broken = false;
            state.messages = [];
        },
        clearMessages: (state) => {
            state.messages = [];
        },
        setConversation: (state, { conversationId }) => {
            state.conversationId = conversationId;
        },
        addNewConversation: (state, { conversationId }) => {
            state.conversations.unshift({
                id: conversationId,
                created: Date.now(),
                title: "New Conversation",
            });
        },

        addMessages: (state, messages) => {
            state.emptyConversation = false;
            for (let message of messages) {
                state.messages.forEach((m) => {
                    if (m.thought) {
                        m.thought.visibility = "hidden";
                    }
                    if (m.rule) {
                        m.rule.state = "dismissed";
                    }
                });

                if (message.thought) {
                    message.thought.visibility = "visible";
                }
                if (message.rule) {
                    message.rule.state = "pending";
                }

                state.messages.push(message);
            }
        },
        addRule: (state, { syntax, valid, id, replace }) => {
            state.emptyConversation = false;
            state.messages.push({
                rule: { syntax, valid, id, state: "pending", replace },
                thread: { id: -1, agent: "root" },
            });
        },
        consumeRule: (state, { rule, type }) => {
            let ruleMessage = state.messages.find((m) => m.rule && m.rule.id === rule.id);
            if (ruleMessage) {
                ruleMessage.rule.state = type;
            }
        },
        addError: (state, { error }) => {
            console.log("addError", error);
            state.messages.push({
                error: { text: error.message, raw: error },
                thread: { id: -1, agent: "root" },
            });
            state.broken = true;
        },
        addThoughts: (state, { thoughts }) => {
            for (let thought of thoughts) {
                if (state.messages.every((m) => m.thought === undefined || m.thought.text !== thought)) {
                    state.messages.push({
                        thought: { text: thought, visibility: "visible" },
                        thread: { id: -1, agent: "root" },
                    });
                }
            }
        },
        setFeedback: (state, { sentiment, text }) => {
            state.feedback.sentiment = sentiment;
            state.feedback.text = text;
        },
        setHealthSummary: (state, healthSummary) => {
            state.healthSummary = healthSummary;
        },
        setContextCache: (state, contextCache) => {
            state.contextCache = [];
            for (let key in contextCache) {
                state.contextCache.push({ key, value: contextCache[key] });
            }
        },
    },
    actions: {
        checkAvailability: async ({ state, rootState, dispatch, commit }) => {
            try {
                let enabled = await chatApi.exec(CHAT_API_TYPE.IS_CHATBOT_ENABLED);
                commit("setEnabled", enabled);
            } catch (error) {
                console.log("Need authentication to check chatbot availability");
            }
        },
        loadAllConversations: async ({ state, rootState, dispatch, commit }) => {
            let conversations = await chatApi.exec(CHAT_API_TYPE.ALL_CONVERSATIONS, {
                root: rootState,
            });
            commit("updateConversations", { conversations });
        },
        getNewConversation: async ({ state, rootState, dispatch, commit }) => {
            commit("clearCurrentConversation");
            commit("setEmptyConversation");
            let conversation = await chatApi.exec(CHAT_API_TYPE.NEW_CONVERSATION, {
                root: rootState,
            });
            commit("setConversation", { conversationId: conversation.id });
            commit("addNewConversation", { conversationId: conversation.id });
            dispatch("loadAllConversations");
        },
        getSpecificConversation: async ({ state, rootState, dispatch, commit }, { conversationId }) => {
            commit("clearCurrentConversation");
            commit("setConversation", { conversationId });
            let { messages, feedback } = await chatApi.exec(CHAT_API_TYPE.GET_CONVERSATION, { root: rootState });
            commit("addMessages", messages);
            commit("setFeedback", feedback);
            dispatch("loadAllConversations");
        },
        inspectConversation: async ({ state, rootState, dispatch, commit }) => {
            let { messages, feedback } = await chatApi.exec(CHAT_API_TYPE.GET_CONVERSATION, {
                root: rootState,
                allMessages: true,
            });
            commit("clearMessages");
            commit("addMessages", messages);
        },
        sendMessage: async ({ state, rootState, dispatch, commit }, { message, context }) => {
            try {
                commit("addMessages", [
                    {
                        message: { text: message, role: "U" },
                        thread: { id: -1, agent: "root" },
                    },
                ]);
                commit("waiting");
                const conversationId = state.conversationId;
                let response = await chatApi.exec(CHAT_API_TYPE.SEND_MESSAGE, {
                    root: rootState,
                    message,
                    context,
                });
                if (conversationId == state.conversationId) {
                    commit("addMessages", [response.message]);
                    if (response.rules) {
                        response.rules.forEach((rule) => {
                            commit("addRule", {
                                ...rule,
                            });
                        });
                    }
                }
            } catch (error) {
                console.log(error);
                commit("addError", { error });
            }
            commit("gotResponse");
        },
        pollThoughts: async ({ state, rootState, dispatch, commit }) => {
            let response = await chatApi.exec(CHAT_API_TYPE.POLLING_THOUGHTS, {
                root: rootState,
            });
            commit("addThoughts", {
                thoughts: response.thoughts,
            });
        },
        provideFeedback: async ({ state, rootState, dispatch, commit }, { sentiment, text }) => {
            sentiment = sentiment || state.feedback.sentiment || "neutral";
            text = text || state.feedback.text || "";
            commit("setFeedback", { sentiment, text });
            await chatApi.exec(CHAT_API_TYPE.PROVIDE_FEEDBACK, {
                root: rootState,
                feedback: { sentiment, text },
            });
        },
        healthCheck: async ({ state, rootState, dispatch, commit }) => {
            commit("setHealthSummary", []);
            let helathSummary = await chatApi.exec(CHAT_API_TYPE.GET_HEALTH_SUMMARY, {
                root: rootState,
            });
            commit("setHealthSummary", helathSummary);
        },
        getContextCache: async ({ state, rootState, dispatch, commit }) => {
            let contextCache = await chatApi.exec(CHAT_API_TYPE.GET_CONTEXT_CACHE, {
                root: rootState,
            });
            commit("setContextCache", contextCache);
        },
        deleteContextCache: async ({ state, rootState, dispatch, commit }) => {
            await chatApi.exec(CHAT_API_TYPE.DELETE_CONTEXT_CACHE, {
                root: rootState,
            });
            commit("setContextCache", {});
        },
        adminLoadAllConversations: async ({ state, rootState, dispatch, commit }) => {
            let conversations = await chatApi.exec(CHAT_API_TYPE.ADMIN_LOAD_ALL_CONVERSATIONS, {
                root: rootState,
            });
            commit("setAdminConversations", conversations);
        },
    },
};
